/* Tree of Treasures. Scoped to its own elements; BOGOS owns gift eligibility. */
(() => {
  'use strict';
  if (!customElements.get('tot-editorial')) {
    customElements.define('tot-editorial', class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        this.mediaQuery = window.matchMedia('(max-width: 749px)');
        this.mediaQuery.addEventListener('change', () => this.mountVideo(), {signal: this.controller.signal});
        this.mountVideo();
      }
      mountVideo() {
        const media = this.querySelector('[data-tot-media]');
        if (!media) return;
        const preferred = this.mediaQuery.matches ? 'mobile' : 'desktop';
        const template = media.querySelector(`template[data-tot-video="${preferred}"]`) || media.querySelector('template[data-tot-video="desktop"]');
        const mount = media.querySelector('[data-tot-video-mount]');
        mount.querySelector('video')?.pause();
        mount.replaceChildren();
        if (template) {
          mount.append(template.content.cloneNode(true));
          const video = mount.querySelector('video');
          video?.setAttribute('aria-label', this.querySelector('h1,h2')?.textContent.trim() || 'Tree of Treasures film');
          // The image pickers also act as explicit posters for each screen size.
          const picture = media.querySelector('picture');
          if (picture && video) {
            const image = picture.querySelector('img');
            const source = this.mediaQuery.matches ? picture.querySelector('source')?.getAttribute('srcset') : null;
            video.poster = source || image.src;
          }
          if (video?.hasAttribute('autoplay')) {
            video.muted = true;
            video.playsInline = true;
            video.play()?.catch(() => { /* Keep controls available if the browser blocks autoplay. */ });
          }
        }
        const picture = media.querySelector('picture');
        if (picture) picture.hidden = Boolean(template);
      }
      disconnectedCallback() {
        this.controller?.abort();
        this.querySelector('video')?.pause();
      }
    });
  }
  if (customElements.get('tree-treasures')) return;
  customElements.define('tree-treasures', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.selection = this.selection || new Map();
      this.addEventListener('change', event => {
        if (event.target.matches('[data-tot-curated-select]')) {
          this.selectCuratedSet(event.target.dataset.totCuratedSelect, true);
          return;
        }
        if (event.target.matches('input[name="id"]')) this.selectVariant(event.target.closest('[data-tot-product]'));
        if (event.target.matches('[data-tot-selection-quantity]') && event.target.reportValidity()) this.changeSelectionQuantity(event.target.dataset.totSelectionQuantity, Number(event.target.value));
        if (event.target.matches('.tot-product--own [name="quantity"]')) {
          const form = event.target.closest('.tot-form');
          const id = form.querySelector('input[name="id"]:checked')?.value;
          if (this.selection.has(id) && form.reportValidity()) this.changeSelectionQuantity(id, Number(event.target.value));
        }
      }, options);
      this.addEventListener('click', event => {
        const collectionAdd = event.target.closest('[data-tot-selection-add]');
        if (collectionAdd) { this.addSelection(); return; }
        const remove = event.target.closest('[data-tot-selection-remove]');
        if (remove) { if (!this.busy) { this.selection.delete(remove.dataset.totSelectionRemove); this.updateSelection(); } return; }
        const layoutButton = event.target.closest('[data-tot-columns]');
        if (layoutButton) {
          this.setColumns(layoutButton.dataset.totColumns);
          return;
        }
        const button = event.target.closest('[data-tot-next],[data-tot-previous]');
        if (!button) return;
        const track = button.closest('[data-tot-gallery]').querySelector('[data-tot-track]');
        const direction = button.hasAttribute('data-tot-next') ? 1 : -1;
        track.scrollBy({left: direction * track.clientWidth, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      }, options);
      this.addEventListener('submit', event => {
        if (!event.target.matches('.tot-form')) return;
        event.preventDefault();
        if (event.target.closest('[data-tot-product]').dataset.totRole === 'own') this.toggleSelection(event.target);
        else this.addProduct(event.target);
      }, options);
      this.querySelectorAll('[data-tot-product]').forEach(product => this.selectVariant(product));
      this.updateSelection();
      const initialCurated = Array.from(this.querySelectorAll('[data-tot-curated-panel]')).find(panel => !panel.hidden);
      if (initialCurated) this.selectCuratedSet(initialCurated.dataset.totCuratedPanel);
      this.addEventListener('shopify:block:select', event => {
        const panel = event.target.closest('[data-tot-curated-panel]');
        if (panel) this.selectCuratedSet(panel.dataset.totCuratedPanel);
      }, options);
      let columns = '2';
      try { columns = localStorage.getItem('lapland-tot-columns') || '2'; } catch { /* Storage can be unavailable in preview. */ }
      this.setColumns(columns);
      const layoutSwitch = this.querySelector('[data-tot-layout-switch]');
      if (layoutSwitch) layoutSwitch.hidden = false;
      this.updateStickyOffset();
      window.addEventListener('scroll', () => this.queueStickyOffset(), {...options, passive: true});
      window.addEventListener('resize', () => this.queueStickyOffset(), options);
      if (typeof ResizeObserver !== 'undefined') {
        this.headerObserver = new ResizeObserver(() => this.queueStickyOffset());
        const header = document.querySelector('.theme__header');
        if (header) this.headerObserver.observe(header);
      }
      window.addEventListener('resize', () => this.querySelectorAll('[data-tot-gallery]').forEach(gallery => this.updateGallery(gallery)), options);
      window.addEventListener('pageshow', event => {
        if (event.persisted) this.unlock();
      }, options);
      // BOGOS remains responsible for gift eligibility and gift cart mutations.
      ['fg-gifts:updated', 'fg-cart:auto-updated'].forEach(name => {
        document.addEventListener(name, () => this.queueCartRefresh(), options);
      });
      document.addEventListener('theme:cart-drawer:open', () => {
        if (!this.cartNeedsRefresh) return;
        this.cartNeedsRefresh = false;
        // Refresh Broadcast's existing drawer only once the shopper opens it.
        document.querySelector('cart-items')?.getCart?.();
      }, options);
    }
    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.cartRefreshTimer);
      this.headerObserver?.disconnect();
      cancelAnimationFrame(this.stickyFrame);
      cancelAnimationFrame(this.layoutFrame);
      cancelAnimationFrame(this.curatedFrame);
      this.cartRefreshVersion = (this.cartRefreshVersion || 0) + 1;
    }
    money(cents) {
      const value = Number(cents) / 100;
      try {
        return new Intl.NumberFormat(this.dataset.locale || 'en', {style: 'currency', currency: this.dataset.currency || 'GBP', currencyDisplay: 'narrowSymbol', minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2}).format(value);
      } catch { return value.toFixed(cents % 100 ? 2 : 0); }
    }
    selectionEntry(form) {
      const card = form.closest('[data-tot-product]');
      if (card?.dataset.totRole !== 'own') return null;
      const id = form.querySelector('input[name="id"]:checked')?.value;
      const template = card.querySelector(`template[data-tot-variant="${id}"]`);
      if (!template || template.dataset.available !== 'true') return null;
      return {id, quantity: Number(form.querySelector('[name="quantity"]').value), price: Number(template.dataset.priceCents), title: card.dataset.totTitle, variant: template.dataset.variantTitle, min: Number(template.dataset.min || 1), step: Number(template.dataset.step || 1), max: template.dataset.max ? Number(template.dataset.max) : null};
    }
    validEntry(entry) {
      return entry && /^\d+$/.test(entry.id) && Number.isSafeInteger(entry.quantity) && entry.quantity >= entry.min && (entry.quantity - entry.min) % entry.step === 0 && (!entry.max || entry.quantity <= entry.max) && Number.isSafeInteger(entry.price) && entry.price >= 0;
    }
    toggleSelection(form) {
      if (this.busy || !form.reportValidity()) return;
      const entry = this.selectionEntry(form);
      if (!this.validEntry(entry)) return;
      if (this.selection.has(entry.id)) this.selection.delete(entry.id);
      else this.selection.set(entry.id, entry);
      this.updateSelection();
    }
    changeSelectionQuantity(id, quantity) {
      if (this.busy) return;
      const entry = this.selection.get(id);
      if (!entry || !this.validEntry({...entry, quantity})) return;
      entry.quantity = quantity;
      this.querySelectorAll('.tot-product--own .tot-form').forEach(form => {
        if (form.querySelector('input[name="id"]:checked')?.value === id) form.querySelector('[name="quantity"]').value = String(quantity);
      });
      this.updateSelection();
    }
    syncOwnCard(card) {
      if (card?.dataset.totRole !== 'own' || !this.selection) return;
      const id = card.querySelector('input[name="id"]:checked')?.value;
      const template = card.querySelector(`template[data-tot-variant="${id}"]`);
      const entry = this.selection.get(id);
      const button = card.querySelector('[data-tot-add]');
      if (!button || !template) return;
      card.classList.toggle('tot-is-selected', Boolean(entry));
      button.textContent = template.dataset.available === 'true' ? (entry ? 'Remove selection' : 'Select product') : 'Sold out';
      button.disabled = Boolean(this.busy) || template.dataset.available !== 'true';
      if (entry) card.querySelector('[name="quantity"]').value = String(entry.quantity);
      const status = card.querySelector('[data-tot-status]');
      status.textContent = entry ? 'Selected for your collection.' : '';
      status.classList.toggle('tot-sr-only', !entry);
      status.classList.toggle('tot-added', Boolean(entry));
    }
    updateSelection() {
      if (!this.selection) return;
      const entries = Array.from(this.selection.values());
      const units = entries.reduce((sum, item) => sum + item.quantity, 0);
      const total = entries.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const panel = this.querySelector('[data-tot-selection]');
      if (!panel) return;
      panel.hidden = false;
      panel.querySelector('[data-tot-selection-count]').textContent = `${units} ${units === 1 ? 'item' : 'items'} selected`;
      panel.querySelector('[data-tot-selection-total]').textContent = this.money(total);
      const button = panel.querySelector('[data-tot-selection-add]');
      button.textContent = this.busy ? 'Adding...' : this.selectionNeedsReview ? 'Review basket' : entries.length === 0 ? 'Select products to add' : entries.length === 1 ? 'Add to cart' : 'Add collection to cart';
      button.disabled = Boolean(this.busy);
      const details = panel.querySelector('[data-tot-selection-details]');
      details.hidden = entries.length === 0;
      const list = panel.querySelector('[data-tot-selection-items]');
      const focusedId = document.activeElement?.getAttribute('data-tot-selection-quantity');
      list.replaceChildren();
      entries.forEach(entry => {
        const row = document.createElement('li');
        const name = document.createElement('span');
        name.textContent = entry.title + (entry.variant && entry.variant !== 'Default Title' ? ' - ' + entry.variant : '');
        name.className = 'tot-selected-name';
        const amount = document.createElement('span');
        amount.textContent = this.money(entry.price * entry.quantity);
        const quantity = document.createElement('input');
        quantity.type = 'number'; quantity.min = String(entry.min); quantity.step = String(entry.step); quantity.value = String(entry.quantity);
        if (entry.max) quantity.max = String(entry.max);
        quantity.setAttribute('data-tot-selection-quantity', entry.id);
        quantity.setAttribute('aria-label', 'Quantity for ' + name.textContent);
        quantity.required = true; quantity.disabled = Boolean(this.busy);
        const remove = document.createElement('button');
        remove.type = 'button'; remove.textContent = 'Remove'; remove.setAttribute('data-tot-selection-remove', entry.id);
        remove.setAttribute('aria-label', 'Remove ' + name.textContent); remove.disabled = Boolean(this.busy);
        row.append(name, amount, quantity, remove); list.append(row);
      });
      if (focusedId) Array.from(list.querySelectorAll('[data-tot-selection-quantity]')).find(input => input.dataset.totSelectionQuantity === focusedId)?.focus({preventScroll: true});
      this.querySelectorAll('.tot-product--own').forEach(card => this.syncOwnCard(card));
      const first = Number(this.dataset.giftFirst), second = Number(this.dataset.giftSecond);
      const previewOK = this.dataset.currency === this.dataset.giftCurrency && first > 0 && second > first;
      const preview = this.querySelector('[data-tot-selection-progress]');
      const live = this.querySelector('[data-tot-live-progress]');
      if (preview) preview.hidden = !previewOK;
      if (live) live.hidden = previewOK;
      const gifts = previewOK ? (total >= second ? 2 : total >= first ? 1 : 0) : 0;
      panel.querySelector('[data-tot-selection-gifts]').textContent = previewOK ? ` - ${gifts} free ${gifts === 1 ? 'gift' : 'gifts'} unlocked` : '';
      if (previewOK && preview) {
        const progress = preview.querySelector('[role="progressbar"]');
        progress.setAttribute('aria-valuemax', String(second)); progress.setAttribute('aria-valuenow', String(Math.min(total, second)));
        progress.setAttribute('aria-valuetext', this.money(total) + ' selected; ' + gifts + ' free ' + (gifts === 1 ? 'gift' : 'gifts') + ' unlocked');
        preview.querySelector('[data-tot-progress-fill]').style.width = Math.min(100, total / second * 100) + '%';
        preview.querySelectorAll('[data-tot-progress-step]').forEach(step => step.classList.toggle('tot-gift-unlocked', gifts >= Number(step.dataset.totProgressStep)));
        preview.querySelector('[data-tot-progress-message]').textContent = total >= second ? 'You have unlocked both gifts for your selection.' : 'Add ' + this.money((total < first ? first : second) - total) + ' more to unlock ' + (total < first ? 'your first free gift.' : 'your next free gift.');
      }
    }
    async addSelection() {
      if (this.busy) return;
      if (this.selectionNeedsReview) { window.location.assign(this.dataset.cartUrl); return; }
      if (!this.selection.size) {
        const grid = this.querySelector('[data-tot-grid]');
        grid?.scrollIntoView({behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start'});
        const first = grid?.querySelector('[data-tot-add]:not(:disabled)');
        first?.focus({preventScroll: true});
        return;
      }
      if (!Array.from(this.selection.values()).every(entry => this.validEntry(entry))) return;
      for (const input of this.querySelectorAll('[data-tot-selection-quantity]')) if (!input.reportValidity()) return;
      for (const form of this.querySelectorAll('.tot-product--own .tot-form')) {
        const id = form.querySelector('input[name="id"]:checked')?.value;
        if (this.selection.has(id) && !form.reportValidity()) return;
      }
      const items = Array.from(this.selection.values(), entry => ({id: Number(entry.id), quantity: entry.quantity}));
      const error = this.querySelector('[data-tot-selection-error]');
      const status = this.querySelector('[data-tot-selection-status]');
      error.hidden = true; status.textContent = 'Adding your selected products to the basket.';
      this.busy = true;
      this.querySelectorAll('.tot-form fieldset,.tot-form [name="quantity"],[data-tot-curated-select]').forEach(input => { input.disabled = true; });
      this.querySelectorAll('[data-tot-add]').forEach(button => { button.disabled = true; });
      this.updateSelection();
      try {
        const response = await fetch(this.dataset.addUrl, {method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest'}, body: JSON.stringify({items})});
        let result;
        try { result = await response.json(); }
        catch { throw new Error('We could not confirm the basket update.'); }
        if (!response.ok || result?.status >= 400) throw new Error(result?.description || result?.message || 'Some products could not be added.');
        if (!Array.isArray(result?.items) || !items.every(item => result.items.some(added => String(added.variant_id || added.id) === String(item.id) && added.quantity >= item.quantity))) throw new Error('We could not confirm every selected product was added.');
        this.selection.clear(); this.cartNeedsRefresh = true; this.unlock();
        status.textContent = 'Your selected products have been added to the basket.';
        this.queueCartRefresh();
        window.location.assign(this.dataset.cartUrl);
      } catch (failure) {
        this.selectionNeedsReview = true;
        error.textContent = (failure instanceof TypeError ? 'We could not confirm the basket update.' : failure.message) + ' Please review your basket before trying again.';
        error.hidden = false; status.textContent = '';
        this.unlock(); error.focus({preventScroll: true});
      }
    }
    selectCuratedSet(value, moveFocus = false) {
      const panels = Array.from(this.querySelectorAll('[data-tot-curated-panel]'));
      const selected = panels.find(panel => panel.dataset.totCuratedPanel === value);
      if (!selected) return;
      panels.forEach(panel => { panel.hidden = panel !== selected; });
      this.querySelectorAll('[data-tot-curated-select]').forEach(input => {
        input.checked = input.dataset.totCuratedSelect === value;
      });
      if (moveFocus) selected.querySelector('[data-tot-curated-select]:checked')?.focus({preventScroll: true});
      cancelAnimationFrame(this.curatedFrame);
      this.curatedFrame = requestAnimationFrame(() => {
        selected.querySelectorAll('[data-tot-gallery]').forEach(gallery => this.updateGallery(gallery));
      });
    }
    setColumns(value) {
      const columns = value === '1' ? '1' : '2';
      const grid = this.querySelector('[data-tot-grid]');
      if (!grid) return;
      grid.dataset.columns = columns;
      this.querySelectorAll('[data-tot-columns]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.totColumns === columns));
      });
      try { localStorage.setItem('lapland-tot-columns', columns); } catch { /* Layout still works without storage. */ }
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = requestAnimationFrame(() => {
        this.querySelectorAll('[data-tot-gallery]').forEach(gallery => this.updateGallery(gallery));
      });
    }
    queueStickyOffset() {
      if (this.stickyFrame) return;
      this.stickyFrame = requestAnimationFrame(() => {
        this.stickyFrame = null;
        this.updateStickyOffset();
      });
    }
    updateStickyOffset() {
      const header = document.querySelector('.theme__header');
      const bounds = header?.getBoundingClientRect();
      const bottom = bounds && bounds.top < window.innerHeight && bounds.bottom > 0 ? bounds.bottom : 0;
      this.style.setProperty('--tot-sticky-top', String(Math.max(0, Math.ceil(bottom)) + 8) + 'px');
    }
    selectVariant(product) {
      if (!product) return;
      const selected = product.querySelector('input[name="id"]:checked');
      if (!selected) return;
      const template = product.querySelector(`template[data-tot-variant="${selected.value}"]`);
      if (!template) return;
      product.querySelector('[data-tot-price]').textContent = template.dataset.price;
      const compare = product.querySelector('[data-tot-compare]');
      compare.textContent = template.dataset.compare;
      compare.hidden = !template.dataset.compare;
      const gallerySlot = product.querySelector('[data-tot-gallery-slot]');
      gallerySlot.replaceChildren(template.content.querySelector('[data-tot-variant-gallery]').cloneNode(true));
      this.initGallery(gallerySlot.querySelector('[data-tot-gallery]'));
      const description = product.querySelector('[data-tot-description]');
      description.replaceChildren(...template.content.querySelector('[data-tot-variant-description]').cloneNode(true).childNodes);
      const button = product.querySelector('[data-tot-add]');
      button.disabled = this.busy || template.dataset.available !== 'true';
      button.textContent = template.dataset.available === 'true' ? 'Add to cart' : 'Sold out';
      const quantity = product.querySelector('[name="quantity"]');
      quantity.min = template.dataset.min;
      quantity.step = template.dataset.step;
      if (template.dataset.max) quantity.max = template.dataset.max;
      else quantity.removeAttribute('max');
      quantity.value = template.dataset.min;
      const error = product.querySelector('[data-tot-error]');
      error.hidden = true;
      error.textContent = '';
      const status = product.querySelector('[data-tot-status]');
      status.textContent = '';
      status.classList.remove('tot-added');
      status.classList.add('tot-sr-only');
      this.syncOwnCard(product);
    }
    initGallery(gallery) {
      if (!gallery) return;
      const track = gallery.querySelector('[data-tot-track]');
      const controls = gallery.querySelector('[data-tot-controls]');
      if (controls) controls.hidden = false;
      track.onscroll = () => this.updateGallery(gallery);
      track.onkeydown = event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        track.scrollBy({left: (event.key === 'ArrowRight' ? 1 : -1) * track.clientWidth});
      };
      this.updateGallery(gallery);
    }
    updateGallery(gallery) {
      const track = gallery.querySelector('[data-tot-track]');
      if (!track || !track.clientWidth) return;
      const count = track.children.length;
      const index = Math.min(count - 1, Math.max(0, Math.round(track.scrollLeft / track.clientWidth)));
      const position = gallery.querySelector('[data-tot-position]');
      if (position) position.textContent = `${index + 1} / ${count}`;
      const previous = gallery.querySelector('[data-tot-previous]');
      const next = gallery.querySelector('[data-tot-next]');
      if (previous) previous.disabled = index === 0;
      if (next) next.disabled = index === count - 1;
    }
    unlock() {
      this.busy = false;
      this.querySelectorAll('[data-tot-curated-select]').forEach(input => { input.disabled = false; });
      this.querySelectorAll('.tot-form').forEach(form => {
        form.removeAttribute('aria-busy');
        form.querySelector('fieldset').disabled = false;
        form.querySelector('[name="quantity"]').disabled = false;
        const template = form.closest('[data-tot-product]').querySelector(`template[data-tot-variant="${form.querySelector('[name="id"]:checked').value}"]`);
        const button = form.querySelector('[data-tot-add]');
        button.disabled = template.dataset.available !== 'true';
        button.textContent = button.disabled ? 'Sold out' : 'Add to cart';
      });
      this.updateSelection();
    }
    queueCartRefresh() {
      clearTimeout(this.cartRefreshTimer);
      // Invalidate an earlier read immediately, before the next read starts.
      this.cartRefreshVersion = (this.cartRefreshVersion || 0) + 1;
      const version = this.cartRefreshVersion;
      this.cartRefreshTimer = setTimeout(() => {
        this.refreshCart(version).catch(() => {
          // The add has succeeded. A failed display refresh must never prompt a duplicate add.
        });
      }, 150);
    }
    async refreshCart(version) {
      const response = await fetch(`${this.dataset.cartUrl}.js`, {
        credentials: 'same-origin', cache: 'no-store', headers: {Accept: 'application/json'},
      });
      if (!response.ok) throw new Error('Cart display could not be refreshed.');
      const cart = await response.json();
      if (version !== this.cartRefreshVersion || !this.isConnected) return;
      if (!Number.isFinite(cart.item_count)) throw new Error('Invalid cart response.');
      // Broadcast uses this event to update its header cart-count components.
      document.dispatchEvent(new CustomEvent('theme:cart:change', {
        bubbles: true, detail: {cartCount: cart.item_count, cart},
      }));
      // Ask the existing BOGOS widget to re-render its own progress calculation.
      document.dispatchEvent(new CustomEvent('bogos:progress-rerender'));
    }
    async addProduct(form) {
      if (this.busy || !form.reportValidity()) return;
      const variantID = form.querySelector('[name="id"]:checked')?.value;
      const product = form.closest('[data-tot-product]');
      const template = product.querySelector(`template[data-tot-variant="${variantID}"]`);
      if (!template || template.dataset.available !== 'true') return;
      const payload = new FormData(form);
      const error = form.querySelector('[data-tot-error]');
      error.hidden = true;
      this.busy = true;
      this.querySelectorAll('[data-tot-curated-select]').forEach(input => { input.disabled = true; });
      form.setAttribute('aria-busy', 'true');
      this.querySelectorAll('[data-tot-add]').forEach(button => { button.disabled = true; });
      form.querySelector('fieldset').disabled = true;
      form.querySelector('[name="quantity"]').disabled = true;
      this.updateSelection();
      form.querySelector('[data-tot-add]').textContent = 'Adding...';
      form.querySelector('[data-tot-status]').textContent = 'Adding your treasures to the cart.';
      try {
        const response = await fetch(this.dataset.addUrl, {method: 'POST', body: payload, credentials: 'same-origin', headers: {Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest'}});
        let result;
        try { result = await response.json(); }
        catch { throw new Error('We could not confirm the cart update. Please check your cart before trying again.'); }
        if (!response.ok || result.status >= 400) throw new Error(result.description || result.message || 'These treasures could not be added. Please check availability and try again.');
        this.cartNeedsRefresh = true;
        this.unlock();
        const status = form.querySelector('[data-tot-status]');
        status.classList.remove('tot-sr-only');
        status.classList.add('tot-added');
        status.textContent = 'Added to your basket. ';
        const link = document.createElement('a');
        link.href = this.dataset.cartUrl;
        link.textContent = 'View basket';
        status.append(link);
        this.queueCartRefresh();
      } catch (failure) {
        error.textContent = failure instanceof TypeError ? 'We could not confirm the cart update. Please check your connection and cart before trying again.' : failure.message;
        error.hidden = false;
        form.querySelector('[data-tot-status]').textContent = '';
        this.unlock();
        error.focus({preventScroll: true});
        error.scrollIntoView({block: 'nearest', behavior: 'auto'});
      }
    }
  });
})();